import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Canceler } from "axios";
import { useAppTable, useFormContainerState, useTrans } from "../../../hooks";
import {
    Button,
    VStack,
    HStack,
    Divider,
    Center,
    Text,
    Hide,
    useToast,
    useDisclosure,
} from "@chakra-ui/react";
import { updatePageIndex } from "ka-table/actionCreators";
import { AppSearch } from "../../../containers";
import {
    AppAlertDelete,
    AppIcon,
    AppLoader,
    AppPageContent,
    AppPageHeader,
} from "../../../components";
import { InternshipScheduleSeatList as ListComponent } from "./InternshipScheduleSeatList";
import { InternshipScheduleSeatForm as FormComponent } from "./InternshipScheduleSeatForm";
import {
    InternshipScheduleSeat as Entity,
    InternshipScheduleSeatApi as EntityApi,
    Internship,
    InternshipApi,
    InternshipSchedule,
    InternshipScheduleApi,
} from "../../../apis";
import { APPC, cPath } from "../../../config";

export const InternshipScheduleSeatPage = () => {
    // hook
    const { t } = useTrans();
    const toast = useToast();
    const { id: internshipId, internshipScheduleId: scheduleId } = useParams<{
        id: string;
        internshipScheduleId: string;
    }>();
    const formContainerState = useFormContainerState();
    const deleteAlertState = useDisclosure();

    // state & const
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [loadingList, setLoadingList] = useState(true);
    const [list, setList] = useState<Entity[]>([]);
    const [internship, setInternship] = useState<Internship | null>(null);
    const [internshipSchedule, setInternshipSchedule] =
        useState<InternshipSchedule | null>(null);
    const [actionSelectId, setActionSelectId] = useState(0);

    const cancelTokenSourcesRef = useRef<Canceler[]>([]);

    const {
        table,
        pageIndex,
        pagesCount,
        pageSize,
        sortColumn,
        sortOrder,
        setPagesCount,
    } = useAppTable("name");

    const paging = {
        enabled: true,
        pageSizes: APPC.PAGE_SIZES,
        pageSize,
        pageIndex,
        pagesCount,
    };

    // handler
    const fetchListData = async () => {
        setLoadingList(true);
        const params = {
            itemsPerPage: pageSize,
        };
        params[`order[${sortColumn}]`] = sortOrder;
        if (internshipId) {
            params[`internship.id`] = internshipId;
        }
        if (scheduleId) {
            params[`internshipSchedule.id`] = scheduleId;
        }
        if (search) {
            params["internship_schedule_seat_search"] = search;
        }

        await EntityApi.getCollection<Entity>(pageIndex + 1, params, (c) => {
            cancelTokenSourcesRef.current.push(c);
        })
            .then(({ errorMessage, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    setPagesCount(Math.ceil(response.totalItems / pageSize));
                    setList(response.items);
                }
            })
            .finally(() => setLoadingList(false));
    };

    const actionClickHandler = (key: string, data?: Entity) => {
        if (!data) {
            return;
        }
        switch (key) {
            case "edit":
                formContainerState.open(data?.id);
                break;
            case "delete":
                setActionSelectId(data?.id);
                deleteAlertState.onOpen();
                break;
        }
    };

    const deleteHandler = async () => {
        if (actionSelectId < 1) {
            return;
        }
        setLoadingList(true);
        await EntityApi.deleteItem(actionSelectId)
            .then(({ errorMessage }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else {
                    fetchListData();
                }
            })
            .finally(() => setLoadingList(false));
    };

    // useEffect
    useEffect(() => {
        if (!formContainerState.isOpen) {
            fetchListData();
        }
    }, [
        pageIndex,
        pageSize,
        sortColumn,
        sortOrder,
        search,
        formContainerState.isOpen,
    ]);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            internshipId
                ? InternshipApi.getItem<Internship>(internshipId)
                : Promise.resolve(null),
            scheduleId
                ? InternshipScheduleApi.getItem<InternshipSchedule>(scheduleId)
                : Promise.resolve(null),
        ])
            .then(([internshipRes, scheduleRes]) => {
                if (internshipRes) {
                    if (internshipRes.errorMessage) {
                        toast({
                            title: internshipRes.errorMessage,
                            status: "error",
                        });
                    } else if (internshipRes.isNotFound) {
                        toast({
                            title: t("cmn:message.entityNotFound"),
                            status: "error",
                        });
                    } else if (internshipRes.response !== null) {
                        setInternship(internshipRes.response);
                    }
                }
                if (scheduleRes) {
                    if (scheduleRes.errorMessage) {
                        toast({
                            title: scheduleRes.errorMessage,
                            status: "error",
                        });
                    } else if (scheduleRes.isNotFound) {
                        toast({
                            title: t("cmn:message.entityNotFound"),
                            status: "error",
                        });
                    } else if (scheduleRes.response !== null) {
                        setInternshipSchedule(scheduleRes.response);
                    }
                }
            })
            .finally(() => setLoading(false));
    }, [internshipId, scheduleId]);

    if (loading) {
        return <AppLoader />;
    }

    return (
        <>
            <AppPageHeader
                title={t("padm.InternshipScheduleSeatPage:text.pageTitle")}
                breadcrumb={
                    internship?.name && internshipSchedule?.name
                        ? [
                              {
                                  label: internship?.name,
                                  url: cPath("ADMIN.INTERNSHIP_PAGE"),
                              },
                              {
                                  label: internshipSchedule?.name,
                                  url: cPath("ADMIN.INTERNSHIP_SCHEDULE_PAGE", {
                                      id: internshipId,
                                  }),
                              },
                          ]
                        : []
                }
            >
                <HStack gap={4} py={{ base: 3.5, sm: 4, md: "1.125rem" }}>
                    <AppSearch
                        onSearch={(value) => {
                            setSearch(value);
                            if (pageIndex > 0) {
                                table.dispatch(updatePageIndex(0));
                            }
                        }}
                    />
                    <Center height="2.1875rem">
                        <Divider
                            orientation="vertical"
                            borderColor={"var(--chakra-colors-greyT80)"}
                        />
                    </Center>
                    <Button
                        variant={"primary"}
                        onClick={() => formContainerState.open(0)}
                    >
                        <AppIcon name="ics-plus-circle" w="1rem" />
                        <Hide below="md">
                            <Text ml={2}>
                                {t(
                                    "padm.InternshipScheduleSeatPage:button.create"
                                )}
                            </Text>
                        </Hide>
                    </Button>
                </HStack>
            </AppPageHeader>
            <AppPageContent>
                <>
                    <VStack p={6}>
                        <ListComponent
                            table={table}
                            paging={paging}
                            list={list}
                            loadingList={loadingList}
                            onActionClick={actionClickHandler}
                        />
                        {deleteAlertState.isOpen && (
                            <AppAlertDelete
                                isOpenDelete={deleteAlertState.isOpen}
                                deleteHandler={() => deleteHandler()}
                                onCloseDelete={deleteAlertState.onClose}
                            />
                        )}
                    </VStack>
                    {formContainerState.isOpen && (
                        <FormComponent
                            formContainerState={formContainerState}
                        />
                    )}
                </>
            </AppPageContent>
        </>
    );
};
